/**
 * GeneratedProjectsPage - Showcase of Erasmus+ project concepts already generated
 * SEO optimized to compete with major EU consultancies
 * Displays structured project ideas ready for development and submission
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  Collapse,
  Divider,
  Alert,
  CircularProgress,
  Pagination,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as BackIcon,
  Lightbulb as IdeaIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingIcon,
  Groups as GroupsIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
  ArrowForward as ArrowIcon,
  FilterList as FilterIcon,
  Send as SendIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { API_URL } from '../config';

interface ProjectTemplate {
  code: string;
  title: string;
  problem_statement: string;
  main_objective: string;
  target_groups: string[];
  expected_results: string[];
  innovation_elements: string;
  eu_priorities: string[];
}

// Predefined filter categories - no need to load from API
const PREDEFINED_FILTERS = [
  // Sectors
  { tag: 'Higher Education', category: 'sector' },
  { tag: 'School Education', category: 'sector' },
  { tag: 'VET', category: 'sector' },
  { tag: 'Adult Education', category: 'sector' },
  { tag: 'Youth', category: 'sector' },
  { tag: 'Sport', category: 'sector' },
  // Priorities
  { tag: 'Digital Transformation', category: 'priority' },
  { tag: 'Inclusion', category: 'priority' },
  { tag: 'Sustainability', category: 'priority' },
  { tag: 'Civic Participation', category: 'priority' },
  // Topics
  { tag: 'Innovation', category: 'topic' },
  { tag: 'Skills Development', category: 'topic' },
  { tag: 'Teacher Training', category: 'topic' },
  { tag: 'Entrepreneurship', category: 'topic' },
  { tag: 'Social Inclusion', category: 'topic' },
  { tag: 'Mental Health', category: 'topic' },
  { tag: 'Green Skills', category: 'topic' },
  { tag: 'AI & Technology', category: 'topic' },
  { tag: 'Intercultural Learning', category: 'topic' },
  { tag: 'Employability', category: 'topic' },
];

interface GeneratedProjectsPageProps {
  onBack: () => void;
  onSubscribe?: () => void;
  onShowContact?: () => void;
  onShowLanding?: () => void;
}

const GeneratedProjectsPage: React.FC<GeneratedProjectsPageProps> = ({ onBack, onSubscribe, onShowContact, onShowLanding }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [projects, setProjects] = useState<ProjectTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dynamicTags, setDynamicTags] = useState<string[]>([]);
  const [searchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const projectsPerPage = 12;
  const [showFilters, setShowFilters] = useState(true);  // Show filters by default
  const isFirstRender = React.useRef(true);
  
  // Request info form states
  const [showRequestForm, setShowRequestForm] = useState<string | null>(null);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const handleRequestSubmit = async (e: React.FormEvent<HTMLFormElement>, projectCode: string) => {
    e.preventDefault();
    setRequestSubmitting(true);
    setRequestError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/myzkpzdv', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setRequestSubmitted(projectCode);
        setShowRequestForm(null);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (err) {
      setRequestError('There was an error. Please try again.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadProjects(true);
    // Load dynamic tags in background (non-blocking)
    loadTagsInBackground();
    // Mark first render as done after initial load
    setTimeout(() => { isFirstRender.current = false; }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load tags from API in background - doesn't block UI
  const loadTagsInBackground = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/showcase/tags`);
      if (response.ok) {
        const data = await response.json();
        const tags = (data.tags || [])
          .filter((t: { tag: string; count: number }) => t.count > 0)
          .map((t: { tag: string }) => t.tag)
          // Filter out tags that are already in predefined filters
          .filter((tag: string) => !PREDEFINED_FILTERS.some(f => f.tag.toLowerCase() === tag.toLowerCase()));
        setDynamicTags(tags.slice(0, 20)); // Limit to 20 additional tags
        console.log('✅ Dynamic tags loaded:', tags.length);
      }
    } catch (error) {
      console.log('ℹ️ Dynamic tags not available, using predefined only');
    }
  };

  // Reload projects when search, tags, or page changes (with debounce for search)
  useEffect(() => {
    // Skip the first render (initial load is handled above)
    if (isFirstRender.current) return;

    const timeoutId = setTimeout(() => {
      console.log('🔍 Search triggered:', { searchTerm, tags: selectedTags, page });
      loadProjects(false);
    }, 150); // 150ms debounce - faster response
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedTags.join(','), page]);

  const loadProjects = async (random: boolean = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm?.trim()) params.append('search', searchTerm.trim());
      if (selectedTags.length > 0) params.append('tags', JSON.stringify(selectedTags));
      if (random) params.append('random', 'true');
      params.append('limit', String(projectsPerPage));
      params.append('offset', String((page - 1) * projectsPerPage));

      const url = `${API_URL}/api/projects/showcase?${params}`;
      console.log('📡 Fetching:', url);
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Response:', { count: data.count, total: data.total, firstProject: data.projects?.[0]?.title });
        setProjects(data.projects || []);
        setTotalProjects(data.total || 0);
      } else {
        console.error('❌ Response error:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag: string) => {
    // Show loading immediately for better UX
    setLoading(true);
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    setPage(1);
  };

  const totalPages = Math.ceil(totalProjects / projectsPerPage);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Navigation */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: '#003399',
          borderBottom: '3px solid #FFCC00'
        }}
      >
        <Toolbar>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ color: 'white' }}
          >
            Back
          </Button>
          <Typography 
            variant="h6" 
            onClick={onShowLanding}
            sx={{ 
              ml: 2, 
              flexGrow: 1, 
              color: 'white', 
              fontWeight: 700,
              cursor: onShowLanding ? 'pointer' : 'default',
              '&:hover': onShowLanding ? { opacity: 0.8 } : {}
            }}
          >
            🎓 Erasmus+ Project Generator
          </Typography>
          {onSubscribe && (
            <Button
              variant="contained"
              onClick={onSubscribe}
              sx={{
                bgcolor: '#FFCC00',
                color: '#003399',
                fontWeight: 700,
                '&:hover': { bgcolor: '#e6b800' }
              }}
            >
              Get Full Access
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section with SEO Content */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `
              radial-gradient(circle at 20% 50%, #FFCC00 3px, transparent 3px),
              radial-gradient(circle at 80% 30%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 60% 70%, #FFCC00 2px, transparent 2px)
            `
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* H1 - Main Title */}
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              color: 'white',
              textAlign: 'center',
              mb: 3
            }}
          >
            Erasmus+ Projects Already Generated
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              maxWidth: 900,
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7
            }}
          >
            Explore <strong>structured project concepts</strong> aligned with Erasmus+ priorities and European cooperation ideas.
            These are not generic examples — they are <strong>starting points ready for development and submission</strong>.
          </Typography>

          {/* Stats Row */}
          <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
            {[
              { value: totalProjects.toLocaleString() + '+', label: 'Project Concepts' },
              { value: '50+', label: 'Thematic Areas' },
              { value: '100%', label: 'EU Aligned' }
            ].map((stat, idx) => (
              <Grid item xs={4} md={3} key={idx}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.8rem', md: '2.5rem' },
                      fontWeight: 800,
                      color: '#FFCC00'
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

        </Container>
      </Box>

      {/* Introduction Section - SEO Rich Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6}>
          {/* What Generated Projects Means */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e0e0', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: '#003399',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IdeaIcon sx={{ color: '#FFCC00', fontSize: 28 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399' }}>
                  What "Generated Projects" Means
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
                A generated project is:
              </Typography>
              <Box component="ul" sx={{ pl: 2, color: '#444' }}>
                {[
                  'A clear project idea with defined objectives',
                  'Aligned with specific Erasmus+ actions and priorities',
                  'Designed for European cooperation',
                  'Adaptable to different national contexts'
                ].map((item, idx) => (
                  <Box component="li" key={idx} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CheckIcon sx={{ color: '#4CAF50', fontSize: 20, mt: 0.3 }} />
                    <Typography variant="body2">{item}</Typography>
                  </Box>
                ))}
              </Box>

              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  These projects are <strong>not yet submitted</strong> and <strong>not owned by any organisation</strong> until developed further.
                </Typography>
              </Alert>
            </Paper>
          </Grid>

          {/* How Organisations Use These */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e0e0', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: '#FFCC00',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <BusinessIcon sx={{ color: '#003399', fontSize: 28 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399' }}>
                  How Organisations Use These
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
                Organisations can use generated projects to:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { icon: <GroupsIcon />, text: 'Join as coordinator or partner' },
                  { icon: <PublicIcon />, text: 'Adapt the concept to their specific context' },
                  { icon: <TrendingIcon />, text: 'Build or join a European partnership' },
                  { icon: <VerifiedIcon />, text: 'Develop a full proposal with strategic support' }
                ].map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ color: '#003399' }}>{item.icon}</Box>
                    <Typography variant="body2" sx={{ color: '#444' }}>{item.text}</Typography>
                  </Box>
                ))}
              </Box>

              {onSubscribe && (
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<ArrowIcon />}
                  onClick={onSubscribe}
                  sx={{
                    mt: 3,
                    bgcolor: '#003399',
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#002266' }
                  }}
                >
                  Access Full Projects via Subscription
                </Button>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Our Role Section */}
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(0, 51, 153, 0.05) 0%, rgba(255, 204, 0, 0.05) 100%)',
            border: '2px solid rgba(0, 51, 153, 0.1)'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399', mb: 3, textAlign: 'center' }}>
            Our Role: Generate and Structure Projects
          </Typography>
          <Grid container spacing={3}>
            {[
              { icon: <IdeaIcon />, title: 'Identify Opportunities', desc: 'We find funding opportunities relevant to your mission' },
              { icon: <SpeedIcon />, title: 'Accelerate Development', desc: 'Cut months of concept development into days' },
              { icon: <VerifiedIcon />, title: 'Reduce Risk', desc: 'Start with proven structures aligned with EU criteria' }
            ].map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#003399',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      '& svg': { color: '#FFCC00', fontSize: 28 }
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Filters Section */}
      <Box sx={{ bgcolor: 'white', py: 3, borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FilterIcon sx={{ color: '#003399' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399' }}>
                Filter by Theme
              </Typography>
              {selectedTags.length > 0 && (
                <Chip
                  label={`${selectedTags.length} selected`}
                  size="small"
                  onDelete={() => setSelectedTags([])}
                  sx={{ bgcolor: '#003399', color: 'white' }}
                />
              )}
            </Box>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: '#003399',
                borderColor: '#003399',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(0, 51, 153, 0.08)' }
              }}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Box>

          <Collapse in={showFilters} timeout={300}>
            <Box sx={{ pt: 2 }}>
              {/* Sectors */}
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, mb: 1, display: 'block' }}>
                SECTORS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {PREDEFINED_FILTERS.filter(f => f.category === 'sector').map((filter) => (
                  <Chip
                    key={filter.tag}
                    label={filter.tag}
                    onClick={() => toggleTag(filter.tag)}
                    variant={selectedTags.includes(filter.tag) ? 'filled' : 'outlined'}
                    sx={{
                      borderColor: '#003399',
                      color: selectedTags.includes(filter.tag) ? 'white' : '#003399',
                      bgcolor: selectedTags.includes(filter.tag) ? '#003399' : 'transparent',
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: selectedTags.includes(filter.tag) ? '#002266' : 'rgba(0, 51, 153, 0.1)'
                      }
                    }}
                  />
                ))}
              </Box>
              
              {/* Priorities */}
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, mb: 1, display: 'block' }}>
                EU PRIORITIES
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {PREDEFINED_FILTERS.filter(f => f.category === 'priority').map((filter) => (
                  <Chip
                    key={filter.tag}
                    label={filter.tag}
                    onClick={() => toggleTag(filter.tag)}
                    variant={selectedTags.includes(filter.tag) ? 'filled' : 'outlined'}
                    sx={{
                      borderColor: '#FFCC00',
                      color: selectedTags.includes(filter.tag) ? '#003399' : '#003399',
                      bgcolor: selectedTags.includes(filter.tag) ? '#FFCC00' : 'transparent',
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: selectedTags.includes(filter.tag) ? '#e6b800' : 'rgba(255, 204, 0, 0.2)'
                      }
                    }}
                  />
                ))}
              </Box>
              
              {/* Topics */}
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, mb: 1, display: 'block' }}>
                TOPICS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {PREDEFINED_FILTERS.filter(f => f.category === 'topic').map((filter) => (
                  <Chip
                    key={filter.tag}
                    label={filter.tag}
                    onClick={() => toggleTag(filter.tag)}
                    variant={selectedTags.includes(filter.tag) ? 'filled' : 'outlined'}
                    sx={{
                      borderColor: '#0066cc',
                      color: selectedTags.includes(filter.tag) ? 'white' : '#0066cc',
                      bgcolor: selectedTags.includes(filter.tag) ? '#0066cc' : 'transparent',
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: selectedTags.includes(filter.tag) ? '#0052a3' : 'rgba(0, 102, 204, 0.1)'
                      }
                    }}
                  />
                ))}
              </Box>
              
              {/* Dynamic tags loaded in background */}
              {dynamicTags.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, mb: 1, mt: 2, display: 'block' }}>
                    MORE TAGS
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {dynamicTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onClick={() => toggleTag(tag)}
                        variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                          borderColor: '#999',
                          color: selectedTags.includes(tag) ? 'white' : '#666',
                          bgcolor: selectedTags.includes(tag) ? '#666' : 'transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: selectedTags.includes(tag) ? '#555' : 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Container>
      </Box>

      {/* Projects Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399' }}>
            {searchTerm || selectedTags.length > 0 
              ? `${totalProjects.toLocaleString()} Projects Found`
              : 'Explore Project Concepts'
            }
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#003399' }} />
            <Typography sx={{ mt: 2, color: '#666' }}>Loading projects...</Typography>
          </Box>
        ) : projects.length === 0 ? (
          <Alert severity="info">
            No projects found matching your criteria. Try adjusting your search or filters.
          </Alert>
        ) : (
          <>
            <Grid container spacing={3}>
              {projects.map((project) => (
                <Grid item xs={12} md={6} lg={4} key={project.code}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      borderRadius: 3,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0, 51, 153, 0.12)',
                        borderColor: '#003399'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Project Code Badge */}
                      <Chip
                        label={project.code}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0, 51, 153, 0.1)',
                          color: '#003399',
                          fontWeight: 600,
                          mb: 2
                        }}
                      />

                      {/* Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#003399',
                          mb: 2,
                          lineHeight: 1.3,
                          minHeight: 52
                        }}
                      >
                        {project.title.length > 80 
                          ? project.title.substring(0, 80) + '...'
                          : project.title
                        }
                      </Typography>

                      {/* Main Objective Preview */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          mb: 2,
                          lineHeight: 1.6,
                          minHeight: 60
                        }}
                      >
                        {project.main_objective.length > 150
                          ? project.main_objective.substring(0, 150) + '...'
                          : project.main_objective
                        }
                      </Typography>

                      {/* Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {project.eu_priorities.slice(0, 3).map((tag, idx) => (
                          <Chip
                            key={idx}
                            label={tag}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              bgcolor: '#FFCC00',
                              color: '#003399',
                              fontWeight: 500
                            }}
                          />
                        ))}
                        {project.eu_priorities.length > 3 && (
                          <Chip
                            label={`+${project.eu_priorities.length - 3}`}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        )}
                      </Box>

                      {/* Expand Button */}
                      <Button
                        size="small"
                        onClick={() => setExpandedProject(
                          expandedProject === project.code ? null : project.code
                        )}
                        endIcon={expandedProject === project.code ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ color: '#003399' }}
                      >
                        {expandedProject === project.code ? 'Show Less' : 'View Details'}
                      </Button>

                      {/* Expanded Content */}
                      <Collapse in={expandedProject === project.code}>
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
                          Problem Statement
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.6 }}>
                          {project.problem_statement}
                        </Typography>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
                          Target Groups
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {project.target_groups.map((tg, idx) => (
                            <Chip key={idx} label={tg} size="small" variant="outlined" />
                          ))}
                        </Box>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
                          Expected Results
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {project.expected_results.slice(0, 3).map((er, idx) => (
                            <Typography component="li" variant="body2" key={idx} sx={{ color: '#666', mb: 0.5 }}>
                              {er}
                            </Typography>
                          ))}
                        </Box>

                        {project.innovation_elements && (
                          <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003399', mt: 2, mb: 1 }}>
                              Innovation Elements
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                              {project.innovation_elements.length > 200
                                ? project.innovation_elements.substring(0, 200) + '...'
                                : project.innovation_elements
                              }
                            </Typography>
                          </>
                        )}

                        {/* Request More Information */}
                        <Divider sx={{ my: 2 }} />
                        
                        {requestSubmitted === project.code ? (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Thank you! We'll contact you soon about project {project.code}
                            </Typography>
                          </Alert>
                        ) : showRequestForm === project.code ? (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
                              Request Information about Project {project.code}
                            </Typography>
                            <form onSubmit={(e) => handleRequestSubmit(e, project.code)}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    required
                                    size="small"
                                    name="name"
                                    label="Your Name"
                                    placeholder="John Smith"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    required
                                    size="small"
                                    name="email"
                                    type="email"
                                    label="Email"
                                    placeholder="john@organization.com"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    name="organization"
                                    label="Organization"
                                    placeholder="Your organization"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    name="country"
                                    label="Country"
                                    placeholder="e.g. Italy, Spain"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    size="small"
                                    name="message"
                                    label="Your Question (optional)"
                                    placeholder="Tell us more about your interest in this project..."
                                  />
                                </Grid>
                                
                                {/* Hidden fields */}
                                <input type="hidden" name="project_code" value={project.code} />
                                <input type="hidden" name="project_title" value={project.title} />
                                <input type="hidden" name="_subject" value={`Info Request: Project ${project.code}`} />

                                {requestError && (
                                  <Grid item xs={12}>
                                    <Alert severity="error">{requestError}</Alert>
                                  </Grid>
                                )}

                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      type="submit"
                                      variant="contained"
                                      size="small"
                                      disabled={requestSubmitting}
                                      startIcon={requestSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                                      sx={{
                                        bgcolor: '#003399',
                                        '&:hover': { bgcolor: '#002266' }
                                      }}
                                    >
                                      {requestSubmitting ? 'Sending...' : 'Send Request'}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => setShowRequestForm(null)}
                                      sx={{ color: '#666', borderColor: '#ccc' }}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </form>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={() => setShowRequestForm(project.code)}
                            sx={{
                              mt: 1,
                              color: '#003399',
                              borderColor: '#003399',
                              '&:hover': {
                                bgcolor: 'rgba(0, 51, 153, 0.08)',
                                borderColor: '#002266'
                              }
                            }}
                          >
                            Request More Information
                          </Button>
                        )}
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size={isMobile ? 'small' : 'large'}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#003399'
                    },
                    '& .Mui-selected': {
                      bgcolor: '#003399 !important',
                      color: 'white !important'
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#003399', py: 8 }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              borderRadius: 4,
              bgcolor: 'white'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#003399', mb: 2 }}>
              Ready to Develop Your Project?
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4, maxWidth: 500, mx: 'auto' }}>
              Get full access to all project concepts and generate custom proposals 
              tailored to your organisation's needs.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {onSubscribe && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={onSubscribe}
                  sx={{
                    bgcolor: '#003399',
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#002266' }
                  }}
                >
                  View Subscription Plans
                </Button>
              )}
              <Button
                variant="outlined"
                size="large"
                onClick={onBack}
                sx={{
                  borderColor: '#003399',
                  color: '#003399',
                  px: 4,
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 51, 153, 0.05)' }
                }}
              >
                Back to Home
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer with SEO content */}
      <Box sx={{ bgcolor: '#001a4d', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                About Generated Erasmus+ Projects
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8 }}>
                Our AI-powered platform generates structured Erasmus+ project concepts covering all Key Actions 
                and priorities. Each project is designed to be scalable at European level, adaptable to different 
                organisations, and ready to be developed into a full proposal. We help universities, NGOs, schools, 
                and training centres find and develop winning project ideas.
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                Key Actions Covered
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['KA1 - Learning Mobility', 'KA2 - Cooperation Partnerships'].map((ka) => (
                  <Typography key={ka} variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    • {ka}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                Target Organisations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['Universities', 'Schools', 'NGOs & Foundations', 'Training Centres', 'Public Bodies'].map((org) => (
                  <Typography key={org} variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    • {org}
                  </Typography>
                ))}
              </Box>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              © {new Date().getFullYear()} Erasmus+ Project Generator. All project concepts are original creations ready for development.
            </Typography>
            {onShowContact && (
              <Button
                variant="text"
                startIcon={<EmailIcon />}
                onClick={onShowContact}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: 'white', bgcolor: 'transparent' }
                }}
              >
                Contact Us
              </Button>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default GeneratedProjectsPage;
